import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { ROUTE_PATHS } from 'routes/constants';
import './Messages.scss';

const Messages = ({ messagesStore }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    // Get search_text from URL query params if present
    const searchParams = new URLSearchParams(location.search);
    const searchText = searchParams.get('search_text');

    if (searchText) {
      // Set the search filter with the search text (customer ID)
      messagesStore.setFilter('search_text', searchText);
    }

    messagesStore.getTopicsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh topics list when navigating back to the messages page
  useEffect(() => {
    // Only refresh if we're on the main messages page (no topic selected)
    if (location.pathname === ROUTE_PATHS.MESSAGES) {
      messagesStore.getTopicsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      // Refresh topics list when navigating with browser buttons
      if (location.pathname === ROUTE_PATHS.MESSAGES || location.pathname.startsWith(ROUTE_PATHS.MESSAGES + '/')) {
        messagesStore.getTopicsList();
      }
    };

    const handleFocus = () => {
      // Refresh when tab gets focus (in case user navigated away and back)
      if (location.pathname === ROUTE_PATHS.MESSAGES || location.pathname.startsWith(ROUTE_PATHS.MESSAGES + '/')) {
        messagesStore.getTopicsList();
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch for URL changes to update filter when coming from customer page
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchText = searchParams.get('search_text');

    if (searchText && messagesStore.filters.search_text !== searchText) {
      messagesStore.setFilter('search_text', searchText);
      messagesStore.getTopicsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (id) {
      // Clear current topic data immediately to show loader
      messagesStore.clearCurrentTopic();
      // Always load topic data fresh from server when ID is present
      messagesStore.clearError();
      messagesStore.clearUploadedFiles();

      // Load topic info and messages directly by ID
      const loadTopicData = async () => {
        try {
          // If store is not initialized, initialize it first
          if (!messagesStore.isInitialized) {
            await messagesStore.getTopicsList();
          }

          // Load topic data (messages will be loaded in Chat component)
          const topicExists = await messagesStore.getTopicById(id);

          if (topicExists) {
            // Mark as read if needed
            const topic = messagesStore.topics.find((t) => t.id === id);
            if (topic && !topic.readByOperator) {
              messagesStore.markTopicAsRead(id);
            }

            // Update current topic index for compatibility
            const topicIndex = messagesStore.getCurrentTopicIndexById(id);
            messagesStore.setCurrentTopicIndex(topicIndex);
          } else {
            // Topic doesn't exist, redirect to list
            navigate(ROUTE_PATHS.MESSAGES);
          }
        } catch (error) {
          console.error('Failed to load topic:', error);
          navigate(ROUTE_PATHS.MESSAGES);
        }
      };

      loadTopicData();
    } else {
      // No topic selected, reset state
      messagesStore.clearCurrentTopic();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <section className={'page-container messages'}>
      <Outlet />
    </section>
  );
};

Messages.propTypes = {
  messagesStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  messagesStore: stores.messagesStore
}))(observer(Messages));
